import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { API_BASE_URL } from "../config";
import vrmLogo from "../assets/VRM_New.jpeg";
import "../styles/buyer.css";

function BuyerSetupPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [tokenStatus, setTokenStatus] = useState({ valid: false, reason: "" });
  const [accountInfo, setAccountInfo] = useState({});

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenStatus({ valid: false, reason: "INVALID" });
      return;
    }

    axios
      .get(`${API_BASE_URL}/api/buyer/setup/validate?token=${token}`)
      .then((res) => {
        setTokenStatus(res.data);
        if (res.data.valid) {
          setAccountInfo(res.data);
        }
      })
      .catch(() => {
        setTokenStatus({ valid: false, reason: "INVALID" });
      })
      .finally(() => {
        setValidating(false);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const res = await axios.post(`${API_BASE_URL}/api/buyer/setup-password`, {
        token: token,
        password: password,
      });

      if (res.data.success) {
        setCompleted(true);
      } else {
        setError(res.data.message || "Failed to set password");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to set password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", fontFamily: "Inter, sans-serif" }}>
        <p style={{ color: "#64748b", fontWeight: "500" }}>Validating your account setup link…</p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      fontFamily: "Inter, sans-serif"
    }}>
      <div style={{
        background: "#ffffff",
        borderRadius: "16px",
        padding: "40px",
        width: "100%",
        maxWidth: "440px",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.3)"
      }}>
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <img src={vrmLogo} alt="VRM AI Logo" style={{ height: "60px", marginBottom: "12px", mixBlendMode: "darken" }} />
          <h2 style={{ margin: "0 0 6px 0", color: "#0f172a", fontSize: "20px", fontWeight: "700" }}>Create Your Buyer Account</h2>
          <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>VRM AI TECHNOLOGY Buyer Portal</p>
        </div>

        {!tokenStatus.valid && !completed && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <AlertCircle size={48} style={{ color: "#dc2626", marginBottom: "12px" }} />
            <h3 style={{ margin: "0 0 8px 0", color: "#991b1b", fontSize: "16px" }}>
              {tokenStatus.reason === "EXPIRED" ? "This account setup link has expired." : "This account setup link is invalid."}
            </h3>
            <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
              Please contact your account administrator or VRM AI support to request a new setup link.
            </p>
            <button
              onClick={() => navigate("/buyer/login")}
              style={{
                padding: "10px 20px",
                background: "#f1f5f9",
                color: "#334155",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                fontWeight: "600",
                cursor: "pointer"
              }}
            >
              Go to Buyer Login
            </button>
          </div>
        )}

        {tokenStatus.valid && !completed && (
          <div>
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", padding: "14px 16px", borderRadius: "8px", marginBottom: "20px", fontSize: "13px" }}>
              <div style={{ color: "#64748b", fontSize: "11px", textTransform: "uppercase", fontWeight: "700", marginBottom: "4px" }}>ACCOUNT DETAILS</div>
              <div><strong>Company:</strong> {accountInfo.companyName}</div>
              <div><strong>Email:</strong> {accountInfo.email}</div>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>New Password</label>
                <div style={{ position: "relative" }}>
                  <Lock size={18} style={{ position: "absolute", left: "14px", top: "12px", color: "#94a3b8" }} />
                  <input
                    type="password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ width: "100%", padding: "10px 14px 10px 42px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Confirm Password</label>
                <div style={{ position: "relative" }}>
                  <Lock size={18} style={{ position: "absolute", left: "14px", top: "12px", color: "#94a3b8" }} />
                  <input
                    type="password"
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ width: "100%", padding: "10px 14px 10px 42px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
              </div>

              {error && (
                <div style={{ background: "#fef2f2", color: "#991b1b", padding: "10px 14px", borderRadius: "8px", fontSize: "13px", marginBottom: "16px" }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#4f46e5",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "15px",
                  cursor: submitting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px"
                }}
              >
                {submitting ? "Creating Account…" : "Create Account"} <ArrowRight size={18} />
              </button>
            </form>
          </div>
        )}

        {completed && (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <CheckCircle2 size={54} style={{ color: "#16a34a", marginBottom: "16px" }} />
            <h3 style={{ margin: "0 0 10px 0", color: "#0f172a", fontSize: "18px" }}>Account Created!</h3>
            <p style={{ fontSize: "14px", color: "#475569", marginBottom: "24px" }}>
              Your Buyer Portal account has been created successfully. You can now log in to view your subscriptions and products.
            </p>
            <button
              onClick={() => navigate("/buyer/login")}
              style={{
                width: "100%",
                padding: "12px",
                background: "#4f46e5",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "15px",
                cursor: "pointer"
              }}
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BuyerSetupPassword;
